/***
 * Module for the Session Video Popup configured in Theme Options.
 * Opens once per browser session, plays a YouTube video inside the popup and
 * falls back to muted playback when the browser blocks autoplay with sound.
****/

import YouTubePlayer from 'youtube-player';

const PLAYER_STATE_PLAYING = 1;
const PLAYER_STATE_BUFFERING = 3;
const PLAYBACK_CHECK_INTERVAL = 150;
const UNMUTED_PLAYBACK_TIMEOUT = 1200;
const MUTED_PLAYBACK_TIMEOUT = 1500;
const BUFFERING_EXTRA_ATTEMPTS = 8;
const PLAYBACK_WATCHDOG_DELAY = 1200;

const hasSessionCookie = ( cookieName ) => document.cookie
	.split( '; ' )
	.some( ( cookie ) => cookie.startsWith( `${ cookieName }=` ) );

const setSessionCookie = ( cookieName ) => {
	const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';

	document.cookie = `${ cookieName }=1; Path=/; SameSite=Lax${ secureAttribute }`;
};

const isPreviewRequest = () => {
	try {
		return new URLSearchParams( window.location.search ).get( 'popup-preview' ) === '1';
	} catch {
		return false;
	}
};

const isWithinSchedule = ( startTime, endTime ) => {
	const now = Math.floor( Date.now() / 1000 );

	if ( startTime && now < startTime ) {
		return false;
	}

	return !endTime || now <= endTime;
};

const wait = ( duration ) => new Promise( ( resolve ) => {
	window.setTimeout( resolve, duration );
} );

// The theme shows a preloader on top of everything until the page is loaded,
// so the popup waits for it instead of opening underneath.
const waitForPageLoad = () => new Promise( ( resolve ) => {
	if ( document.readyState === 'complete' ) {
		resolve();
		return;
	}

	window.addEventListener( 'load', resolve, { once: true } );
} );

/**
 * Create a controller that owns the YouTube player of the popup.
 *
 * @param {jQuery} $popup The popup element.
 * @return {Object} The player controller.
 */
const createPlayerController = ( $popup ) => {
	const $container = $popup.find( '.modal-session-video__player' );
	const videoID = String( $popup.attr( 'data-video-id' ) || '' );
	let player = null;

	// The YouTube API replaces the given element with an iframe, so a throwaway
	// child element is used to keep the styled container in place.
	const createPlayer = ( muted ) => {
		const $target = $( '<div />' ).appendTo( $container.empty() );

		player = YouTubePlayer( $target[0], {
			videoId: videoID,
			playerVars: {
				autoplay: 1,
				mute: muted ? 1 : 0,
				rel: 0,
				playsinline: 1,
			}
		} );
	};

	const getState = async() => {
		try {
			return await player.getPlayerState();
		} catch {
			return null;
		}
	};

	const isPlaying = async() => await getState() === PLAYER_STATE_PLAYING;

	// Only actual playback counts as a success: browsers do start buffering an
	// unmuted video and pause it right after. Buffering extends the waiting time.
	const waitForPlayback = async( timeout ) => {
		let attemptsLeft = Math.ceil( timeout / PLAYBACK_CHECK_INTERVAL );

		while ( attemptsLeft > 0 ) {
			attemptsLeft -= 1;

			await wait( PLAYBACK_CHECK_INTERVAL );

			const state = await getState();

			if ( state === PLAYER_STATE_PLAYING ) {
				return true;
			}

			if ( state === PLAYER_STATE_BUFFERING ) {
				attemptsLeft = Math.max( attemptsLeft, BUFFERING_EXTRA_ATTEMPTS );
			}
		}

		return false;
	};

	const playMuted = async() => {
		try {
			await player.mute();
			await player.playVideo();
		} catch {
			return false;
		}

		return waitForPlayback( MUTED_PLAYBACK_TIMEOUT );
	};

	return {
		isPlaying,

		playMuted,

		/**
		 * Start the video automatically, keeping the sound on when the browser allows it.
		 *
		 * @return {Promise<string>} `sound`, `muted` or `blocked`.
		 */
		startAutoplay: async() => {
			createPlayer( false );

			if ( await waitForPlayback( UNMUTED_PLAYBACK_TIMEOUT ) ) {
				return 'sound';
			}

			return await playMuted() ? 'muted' : 'blocked';
		},

		/**
		 * Start the video with sound after a user interaction.
		 *
		 * @return {Promise<void>}
		 */
		startWithSound: async() => {
			if ( !player ) {
				createPlayer( false );
				return;
			}

			try {
				await player.unMute();
				await player.playVideo();
			} catch {
				createPlayer( false );
			}
		},

		/**
		 * Turn the sound on after a user interaction.
		 *
		 * @return {Promise<void>}
		 */
		unmute: async() => {
			if ( !player ) {
				return;
			}

			try {
				await player.unMute();
				await player.playVideo();
			} catch {
				// The player is already gone, nothing to unmute.
			}
		},

		destroy: () => {
			if ( player ) {
				player.destroy();
				player = null;
			}

			$container.empty();
		}
	};
};

// Another popup (a video modal, a form modal) is already open or opening.
const isAnotherModalOpen = () => $( '.modal.show' ).length > 0 || $( 'html' ).hasClass( 'modal-open' );

/**
 * Open the popup once the page is loaded and the configured delay has passed.
 *
 * @param {jQuery}  $popup The popup element.
 * @param {boolean} previewRequest Whether the session cookie must be ignored.
 * @return {Promise<void>}
 */
const openPopup = async( $popup, previewRequest ) => {
	const cookieName = String( $popup.attr( 'data-cookie-name' ) || '' );
	const openingDelay = Number( $popup.attr( 'data-opening-delay' ) ) || 0;

	await waitForPageLoad();
	await wait( openingDelay );

	// The session cookie is intentionally left untouched here, so the popup
	// gets another chance during the next session.
	if ( isAnotherModalOpen() ) {
		return;
	}

	if ( !previewRequest ) {
		if ( hasSessionCookie( cookieName ) ) {
			return;
		}

		setSessionCookie( cookieName );
	}

	$popup.modal( 'show' );
};

/**
 * Bind the popup behaviour and schedule its opening.
 *
 * @param {jQuery}  $popup The popup element.
 * @param {boolean} previewRequest Whether the session cookie must be ignored.
 * @return {void}
 */
const initPopup = ( $popup, previewRequest ) => {
	const controller = createPlayerController( $popup );
	const $sound = $popup.find( '.modal-session-video__sound' );
	const autoplayEnabled = String( $popup.attr( 'data-autoplay' ) ) === '1';
	let userInteracted = false;

	const toggleSoundPrompt = ( visible ) => {
		$sound.prop( 'hidden', !visible );
	};

	// Some browsers start an unmuted video and pause it a moment later, so the
	// playback is checked again and downgraded to muted playback when needed.
	const watchUnmutedPlayback = async() => {
		await wait( PLAYBACK_WATCHDOG_DELAY );

		if ( userInteracted || await controller.isPlaying() ) {
			return;
		}

		toggleSoundPrompt( await controller.playMuted() );
	};

	const handleAutoplay = async() => {
		$popup.addClass( 'is-starting' );

		const result = await controller.startAutoplay();

		$popup.removeClass( 'is-starting' );

		if ( result === 'blocked' ) {
			return;
		}

		$popup.addClass( 'is-playing' );
		toggleSoundPrompt( result === 'muted' );

		if ( result === 'sound' ) {
			watchUnmutedPlayback();
		}
	};

	$popup.find( '.modal-session-video__trigger' ).on( 'click', () => {
		userInteracted = true;
		toggleSoundPrompt( false );
		$popup.addClass( 'is-playing' );
		controller.startWithSound();
	} );

	$popup.find( '.modal-session-video__media' ).on( 'pointerdown', () => {
		userInteracted = true;
	} );

	$sound.on( 'click', () => {
		userInteracted = true;
		toggleSoundPrompt( false );
		controller.unmute();
	} );

	$popup.on( 'show.bs.modal', () => {
		$( 'html' ).addClass( 'modal-open' );
	} );

	$popup.on( 'shown.bs.modal', () => {
		if ( autoplayEnabled ) {
			handleAutoplay();
		}
	} );

	$popup.on( 'hide.bs.modal', () => {
		controller.destroy();
		$popup.removeClass( 'is-playing is-starting' );
		toggleSoundPrompt( false );
	} );

	$popup.on( 'hidden.bs.modal', () => {
		$( 'html' ).removeClass( 'modal-open' );
	} );

	openPopup( $popup, previewRequest );
};

/**
 * Check whether the popup may open for the current visitor.
 *
 * @param {jQuery} $popup The popup element.
 * @return {boolean}
 */
const canOpenPopup = ( $popup ) => {
	const cookieName = String( $popup.attr( 'data-cookie-name' ) );
	const startTime = Number( $popup.attr( 'data-start-time' ) ) || 0;
	const endTime = Number( $popup.attr( 'data-end-time' ) ) || 0;

	return isWithinSchedule( startTime, endTime ) && !hasSessionCookie( cookieName );
};

const modalSessionVideo = () => {
	const $popup = $( '#modal-session-video' );

	if ( !$popup.length ) {
		return;
	}

	if ( !$popup.attr( 'data-video-id' ) || !$popup.attr( 'data-cookie-name' ) ) {
		return;
	}

	// The preview request ignores both the session cookie and the schedule.
	if ( isPreviewRequest() ) {
		initPopup( $popup, true );
		return;
	}

	if ( canOpenPopup( $popup ) ) {
		initPopup( $popup, false );
	}
};

export default modalSessionVideo;
